import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { createTask } from '../features/tasks/taskSlice';
import { showToast } from '../features/ui/uiSlice';
import { RootState } from '../store/store';
import api from '../utils/axiosConfig';
import SkillsDropdown from './UI/SkillsDropdown';
import {
    ClipboardDocumentListIcon,
    CurrencyDollarIcon,
    MapPinIcon,
    UserGroupIcon,
    CalendarIcon,
    DocumentTextIcon,
    CogIcon,
    CheckCircleIcon,
    PlusIcon,
    TrashIcon,
    BuildingOfficeIcon,
    XMarkIcon,
    PencilIcon
} from '@heroicons/react/24/outline';

interface TaskFormProps {
    onSuccess?: () => void;
}

const TaskForm: React.FC<TaskFormProps> = ({ onSuccess }) => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state: RootState) => state.auth);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [companies, setCompanies] = useState<Array<{ _id: string, companyName: string }>>([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [showBudgetScenarioModal, setShowBudgetScenarioModal] = useState(false);
    const [editingScenario, setEditingScenario] = useState<any>(null);
    const [newScenario, setNewScenario] = useState({
        name: '',
        description: '',
        range: {
            min: 1,
            max: 500
        },
        budgetDistribution: {
            teamExecutive: 0,
            teamLeader: 0,
            districtHead: 0,
            stateHead: 0,
            regionalHead: 0,
            admin: 0,
        },
        isActive: false
    });
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        taskUrl: '',
        isOnlineTask: false,
        totalBudget: '',
        budgetScenarios: [] as Array<{
            id: string;
            name: string;
            description: string;
            range: {
                min: number;
                max: number;
            };
            budgetDistribution: {
                teamExecutive: number;
                teamLeader: number;
                districtHead: number;
                stateHead: number;
                regionalHead: number;
                admin: number;
            };
            isActive: boolean;
        }>,
        currentScenario: null as string | null,
        regions: [] as string[],
        states: [] as string[],
        districts: [] as string[],
        targetDesignations: ['team_executive', 'team_leader', 'district_head', 'state_head', 'regional_head'],
        requiredSkills: [] as string[],
        startDate: '',
        endDate: '',
        validationRules: {
            requiredFields: [] as string[],
        },
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (name.startsWith('validationRules.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                validationRules: {
                    ...prev.validationRules,
                    [field]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleArrayChange = (name: string, value: string[]) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRegionChange = (region: string, isChecked: boolean) => {
        let newRegions = [...formData.regions];
        let newStates = [...formData.states];
        let newDistricts = [...formData.districts];

        if (isChecked) {
            // Add region
            newRegions.push(region);
            // Add all states in this region
            const regionStates = regionStatesMap[region as keyof typeof regionStatesMap] || [];
            regionStates.forEach(state => {
                if (!newStates.includes(state)) {
                    newStates.push(state);
                }
            });
            // Add all districts in these states
            regionStates.forEach(stateName => {
                const state = stateData.find(s => s.name === stateName);
                if (state) {
                    state.districts.forEach(district => {
                        if (!newDistricts.includes(district)) {
                            newDistricts.push(district);
                        }
                    });
                }
            });
        } else {
            // Remove region
            newRegions = newRegions.filter(r => r !== region);
            // Remove all states in this region
            const regionStates = regionStatesMap[region as keyof typeof regionStatesMap] || [];
            newStates = newStates.filter(state => !regionStates.includes(state));
            // Remove all districts in these states
            regionStates.forEach(stateName => {
                const state = stateData.find(s => s.name === stateName);
                if (state) {
                    newDistricts = newDistricts.filter(district => !state.districts.includes(district));
                }
            });
        }

        setFormData(prev => ({
            ...prev,
            regions: newRegions,
            states: newStates,
            districts: newDistricts
        }));

        // Update designations based on new scope
        updateDesignationsBasedOnScope(newRegions, newStates);
    };

    const updateDesignationsBasedOnScope = (regions: string[], states: string[]) => {
        let newDesignations = [...formData.targetDesignations];

        if (regions.length > 0) {
            // If at least one region is selected, select all designations including regional head
            newDesignations = ['team_executive', 'team_leader', 'district_head', 'state_head', 'regional_head'];
        } else if (states.length > 0) {
            // If no regions but at least one state, select all except regional head
            newDesignations = ['team_executive', 'team_leader', 'district_head', 'state_head'];
        } else {
            // If no regions and no states, keep current selections but don't auto-select regional head
            // This allows manual selection
        }

        setFormData(prev => ({
            ...prev,
            targetDesignations: newDesignations
        }));
    };

    const handleStateChange = (stateName: string, isChecked: boolean) => {
        let newStates = [...formData.states];
        let newDistricts = [...formData.districts];

        if (isChecked) {
            newStates.push(stateName);
            // Add all districts of this state
            const state = stateData.find(s => s.name === stateName);
            if (state) {
                state.districts.forEach(district => {
                    if (!newDistricts.includes(district)) {
                        newDistricts.push(district);
                    }
                });
            }
        } else {
            newStates = newStates.filter(s => s !== stateName);
            // Remove districts of this state
            const state = stateData.find(s => s.name === stateName);
            if (state) {
                newDistricts = newDistricts.filter(d => !state.districts.includes(d));
            }
        }

        setFormData(prev => ({
            ...prev,
            states: newStates,
            districts: newDistricts
        }));

        // Update designations based on new scope
        updateDesignationsBasedOnScope(formData.regions, newStates);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            taskUrl: '',
            isOnlineTask: false,
            totalBudget: '',
            budgetScenarios: [] as Array<{
                id: string;
                name: string;
                description: string;
                range: {
                    min: number;
                    max: number;
                };
                budgetDistribution: {
                    teamExecutive: number;
                    teamLeader: number;
                    districtHead: number;
                    stateHead: number;
                    regionalHead: number;
                    admin: number;
                };
                isActive: boolean;
            }>,
            currentScenario: null as string | null,
            regions: [] as string[],
            states: [] as string[],
            districts: [] as string[],
            targetDesignations: ['team_executive', 'team_leader', 'district_head', 'state_head', 'regional_head'],
            requiredSkills: [] as string[],
            startDate: '',
            endDate: '',
            validationRules: {
                requiredFields: [] as string[],
            },
        });
        setSelectedCompany('');
    };

    const handleValidationFieldChange = (field: string) => {
        setFormData(prev => ({
            ...prev,
            validationRules: {
                requiredFields: prev.validationRules.requiredFields.includes(field)
                    ? prev.validationRules.requiredFields.filter(f => f !== field)
                    : [...prev.validationRules.requiredFields, field]
            }
        }));
    };

    const addValidationField = () => {
        const fieldName = prompt('Enter field name:');
        if (fieldName && fieldName.trim()) {
            handleValidationFieldChange(fieldName.trim());
        }
    };

    // Budget Scenario Management Functions
    const addBudgetScenario = () => {
        const scenario = {
            id: Date.now().toString(),
            name: newScenario.name,
            description: newScenario.description,
            range: newScenario.range,
            budgetDistribution: newScenario.budgetDistribution,
            isActive: true // All scenarios are active by default
        };

        setFormData(prev => ({
            ...prev,
            budgetScenarios: [...prev.budgetScenarios, scenario],
            currentScenario: formData.budgetScenarios.length === 0 ? scenario.id : prev.currentScenario
        }));

        setNewScenario({
            name: '',
            description: '',
            range: {
                min: 1,
                max: 500
            },
            budgetDistribution: {
                teamExecutive: 0,
                teamLeader: 0,
                districtHead: 0,
                stateHead: 0,
                regionalHead: 0,
                admin: 0,
            },
            isActive: false
        });
        setShowBudgetScenarioModal(false);
    };

    const updateBudgetScenario = (scenarioId: string, updatedScenario: any) => {
        setFormData(prev => ({
            ...prev,
            budgetScenarios: prev.budgetScenarios.map(scenario =>
                scenario.id === scenarioId ? updatedScenario : scenario
            )
        }));
        setEditingScenario(null);
        setShowBudgetScenarioModal(false);
    };

    const deleteBudgetScenario = (scenarioId: string) => {
        setFormData(prev => ({
            ...prev,
            budgetScenarios: prev.budgetScenarios.filter(scenario => scenario.id !== scenarioId)
        }));
    };

    const toggleBudgetScenario = (scenarioId: string) => {
        setFormData(prev => ({
            ...prev,
            budgetScenarios: prev.budgetScenarios.map(scenario => ({
                ...scenario,
                isActive: scenario.id === scenarioId ? !scenario.isActive : scenario.isActive
            }))
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Clean budget scenarios - remove any empty or invalid scenarios
            const cleanBudgetScenarios = formData.budgetScenarios.filter(scenario => {
                const isValid = scenario.name &&
                    scenario.name.trim() !== '' &&
                    scenario.range &&
                    typeof scenario.range.min === 'number' &&
                    typeof scenario.range.max === 'number' &&
                    scenario.range.min < scenario.range.max &&
                    scenario.budgetDistribution &&
                    Object.values(scenario.budgetDistribution).every(val => typeof val === 'number' && val >= 0);

                if (!isValid) {
                    console.log('Removing invalid scenario:', scenario);
                }
                return isValid;
            });

            // All scenarios are active by default, no need to check

            // Create task data with all required fields
            const taskData = {
                title: formData.title,
                description: formData.description,
                taskUrl: formData.taskUrl,
                isOnlineTask: formData.isOnlineTask,
                totalBudget: parseFloat(formData.totalBudget),
                budgetScenarios: cleanBudgetScenarios,
                currentScenario: cleanBudgetScenarios.length > 0 ? formData.currentScenario : null,
                regions: formData.regions,
                states: formData.states,
                districts: formData.districts,
                targetDesignations: formData.targetDesignations,
                requiredSkills: formData.requiredSkills,
                startDate: formData.startDate,
                endDate: formData.endDate,
                validationRules: {
                    requiredFields: formData.validationRules.requiredFields || []
                },
                ...(user?.role === 'admin' && selectedCompany && { company: selectedCompany })
            };

            console.log('Sending task data:', taskData); // Debug log
            console.log('Budget scenarios:', cleanBudgetScenarios); // Debug log

            await dispatch(createTask(taskData as any)).unwrap();

            dispatch(showToast({
                message: 'Task created successfully!',
                type: 'success'
            }));

            // Reset form after successful creation
            resetForm();

            if (onSuccess) {
                onSuccess();
            }
        } catch (error: any) {
            console.error('Task creation error:', error); // Debug log
            console.error('Error details:', error);

            let errorMessage = 'Failed to create task';
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (error?.payload) {
                errorMessage = error.payload;
            }

            dispatch(showToast({
                message: errorMessage,
                type: 'error'
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchCompanies = useCallback(async () => {
        try {
            const response = await api.get('/companies');
            setCompanies(response.data.data);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    }, []);

    useEffect(() => {
        if (user?.role === 'admin') {
            fetchCompanies();
        }
    }, [user?.role, fetchCompanies]);

    const regions = ['north', 'south', 'east', 'west', 'central'];
    const designations = ['team_executive', 'team_leader', 'district_head', 'state_head', 'regional_head'];

    // Region to states mapping
    const regionStatesMap = {
        north: ['Jammu and Kashmir', 'Himachal Pradesh', 'Punjab', 'Haryana', 'Delhi', 'Uttarakhand', 'Uttar Pradesh', 'Rajasthan'],
        south: ['Andhra Pradesh', 'Karnataka', 'Kerala', 'Tamil Nadu', 'Telangana', 'Puducherry'],
        east: ['West Bengal', 'Odisha', 'Jharkhand', 'Bihar', 'Assam', 'Arunachal Pradesh', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Sikkim', 'Tripura'],
        west: ['Maharashtra', 'Gujarat', 'Goa', 'Dadra and Nagar Haveli', 'Daman and Diu'],
        central: ['Madhya Pradesh', 'Chhattisgarh']
    };
    const defaultValidationFields = [
        'name', 'email', 'phone', 'address',
        'age', 'qualification', 'experience',
        'documents', 'id_proof', 'photo',
        'parent_name', 'parent_phone', 'parent_email', 'family_income'
    ];

    // Comprehensive State and district data
    const stateData = [
        { name: 'Andhra Pradesh', districts: ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Tirupati', 'Rajahmundry', 'Kadapa', 'Anantapur', 'Chittoor', 'Ongole', 'Eluru', 'Machilipatnam', 'Tenali', 'Proddatur', 'Chilakaluripet', 'Kadiri', 'Gudivada', 'Srikakulam', 'Vizianagaram'] },
        { name: 'Arunachal Pradesh', districts: ['Itanagar', 'Naharlagun', 'Pasighat', 'Tezpur', 'Bomdila', 'Tawang', 'Ziro', 'Along', 'Roing', 'Daporijo', 'Anini', 'Changlang', 'Khonsa', 'Seppa', 'Koloriang', 'Yingkiong', 'Aalo', 'Miao', 'Namsai'] },
        { name: 'Assam', districts: ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Tezpur', 'Nagaon', 'Tinsukia', 'Barpeta', 'Dhubri', 'Kokrajhar', 'Goalpara', 'Bongaigaon', 'Mangaldoi', 'Karimganj', 'Hailakandi', 'Diphu', 'North Lakhimpur', 'Sivasagar', 'Golaghat', 'Morigaon'] },
        { name: 'Bihar', districts: ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga', 'Purnia', 'Arrah', 'Begusarai', 'Katihar', 'Munger', 'Chapra', 'Bettiah', 'Motihari', 'Hajipur', 'Sitamarhi', 'Samastipur', 'Saharsa', 'Madhubani', 'Siwan', 'Buxar'] },
        { name: 'Chhattisgarh', districts: ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Rajnandgaon', 'Durg', 'Raigarh', 'Jagdalpur', 'Ambikapur', 'Dhamtari', 'Mahasamund', 'Kanker', 'Janjgir-Champa', 'Mungeli', 'Kabirdham', 'Sukma', 'Kondagaon', 'Balrampur', 'Surajpur', 'Balod'] },
        { name: 'Goa', districts: ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Mormugao', 'Quepem', 'Sanguem', 'Canacona', 'Bicholim', 'Pernem', 'Sattari', 'Tiswadi', 'Bardez', 'Salcete', 'Dharbandora'] },
        { name: 'Gujarat', districts: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Junagadh', 'Gandhinagar', 'Anand', 'Navsari', 'Mehsana', 'Bharuch', 'Surendranagar', 'Gandhidham', 'Nadiad', 'Morbi', 'Palanpur', 'Veraval', 'Godhra'] },
        { name: 'Haryana', districts: ['Chandigarh', 'Faridabad', 'Gurgaon', 'Panipat', 'Karnal', 'Hisar', 'Rohtak', 'Ambala', 'Sonipat', 'Yamunanagar', 'Kaithal', 'Kurukshetra', 'Rewari', 'Jind', 'Sirsa', 'Bhiwani', 'Palwal', 'Fatehabad', 'Mahendragarh', 'Nuh'] },
        { name: 'Himachal Pradesh', districts: ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Kullu', 'Chamba', 'Una', 'Bilaspur', 'Hamirpur', 'Kangra', 'Kinnaur', 'Lahaul and Spiti', 'Sirmaur', 'Palampur', 'Nahan', 'Rampur', 'Sundernagar', 'Paonta Sahib', 'Nalagarh', 'Sujanpur'] },
        { name: 'Jharkhand', districts: ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Deoghar', 'Hazaribagh', 'Giridih', 'Palamu', 'Chatra', 'Koderma', 'Garhwa', 'Simdega', 'Lohardaga', 'Gumla', 'Pakur', 'Sahebganj', 'Godda', 'Dumka', 'Jamtara', 'Saraikela'] },
        { name: 'Karnataka', districts: ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belgaum', 'Gulbarga', 'Davanagere', 'Bellary', 'Bijapur', 'Shimoga', 'Tumkur', 'Raichur', 'Bidar', 'Hospet', 'Hassan', 'Mandya', 'Chitradurga', 'Kolar', 'Chikmagalur', 'Udupi'] },
        { name: 'Kerala', districts: ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kannur', 'Kollam', 'Alappuzha', 'Palakkad', 'Malappuram', 'Ernakulam', 'Idukki', 'Kottayam', 'Wayanad', 'Kasargod', 'Pathanamthitta', 'Calicut'] },
        { name: 'Madhya Pradesh', districts: ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Dewas', 'Satna', 'Ratlam', 'Rewa', 'Murwara', 'Singrauli', 'Burhanpur', 'Khandwa', 'Mandsaur', 'Neemuch', 'Chhindwara', 'Hoshangabad', 'Itarsi', 'Sehore'] },
        { name: 'Maharashtra', districts: ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Aurangabad', 'Solapur', 'Amravati', 'Kolhapur', 'Sangli', 'Malegaon', 'Jalgaon', 'Akola', 'Latur', 'Ahmadnagar', 'Dhule', 'Chandrapur', 'Parbhani', 'Ichalkaranji', 'Jalna'] },
        { name: 'Manipur', districts: ['Imphal', 'Thoubal', 'Bishnupur', 'Churachandpur', 'Senapati', 'Tamenglong', 'Chandel', 'Ukhrul', 'Kangpokpi', 'Jiribam', 'Kakching', 'Tengnoupal', 'Kamjong', 'Noney', 'Pherzawl', 'Imphal East', 'Imphal West'] },
        { name: 'Meghalaya', districts: ['Shillong', 'Tura', 'Jowai', 'Nongstoin', 'Williamnagar', 'Baghmara', 'Resubelpara', 'Mairang', 'Mawkyrwat', 'Ampati', 'Khliehriat', 'Mawphlang', 'Sohra', 'Nongpoh', 'Mawryngkneng', 'Pynursla', 'Mawshynrut', 'Ranikor'] },
        { name: 'Mizoram', districts: ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib', 'Serchhip', 'Mamit', 'Lawngtlai', 'Saitual', 'Hnahthial', 'Khawzawl', 'Darlawn', 'Tlabung', 'Vairengte', 'Bilkhawthlir', 'West Phaileng', 'Ngopa', 'Reiek', 'Thenzawl'] },
        { name: 'Nagaland', districts: ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto', 'Phek', 'Mon', 'Longleng', 'Kiphire', 'Peren', 'Noklak', 'Shamator', 'Tseminyu', 'Chumukedima', 'Niuland', 'Medziphema', 'Jalukie', 'Pfutsero', 'Meluri'] },
        { name: 'Odisha', districts: ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Balasore', 'Bhadrak', 'Baripada', 'Jharsuguda', 'Bargarh', 'Sundargarh', 'Kendujhar', 'Mayurbhanj', 'Baleswar', 'Jajpur', 'Dhenkanal', 'Angul', 'Deogarh', 'Kandhamal'] },
        { name: 'Punjab', districts: ['Chandigarh', 'Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Mohali', 'Moga', 'Firozpur', 'Batala', 'Abohar', 'Malerkotla', 'Khanna', 'Phagwara', 'Muktsar', 'Barnala', 'Rajpura', 'Fazilka', 'Kapurthala', 'Sunam'] },
        { name: 'Rajasthan', districts: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner', 'Bharatpur', 'Alwar', 'Bhilwara', 'Ganganagar', 'Sikar', 'Pali', 'Tonk', 'Sawai Madhopur', 'Churu', 'Jhunjhunu', 'Nagaur', 'Banswara', 'Dungarpur', 'Pratapgarh'] },
        { name: 'Sikkim', districts: ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Soreng', 'Ravongla', 'Jorethang', 'Singtam', 'Rangpo', 'Pakyong', 'Rongli', 'Dikchu', 'Chungthang', 'Lachen', 'Lachung', 'Yuksom', 'Pelling', 'Ravangla'] },
        { name: 'Tamil Nadu', districts: ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Tiruppur', 'Erode', 'Vellore', 'Thoothukkudi', 'Dindigul', 'Thanjavur', 'Ranipet', 'Sivaganga', 'Karur', 'Hosur', 'Nagercoil', 'Kanchipuram', 'Cuddalore', 'Kumbakonam'] },
        { name: 'Telangana', districts: ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam', 'Karimnagar', 'Ramagundam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Miryalaguda', 'Siddipet', 'Nirmal', 'Jagtial', 'Mancherial', 'Kamareddy', 'Sangareddy', 'Medak', 'Vikarabad', 'Wanaparthy'] },
        { name: 'Tripura', districts: ['Agartala', 'Dharmanagar', 'Udaipur', 'Kailashahar', 'Belonia', 'Khowai', 'Teliamura', 'Ambassa', 'Kamalpur', 'Sabroom', 'Sonamura', 'Melaghar', 'Bishalgarh', 'Jirania', 'Dukli', 'Hezamara'] },
        { name: 'Uttar Pradesh', districts: ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Meerut', 'Allahabad', 'Bareilly', 'Ghaziabad', 'Moradabad', 'Aligarh', 'Saharanpur', 'Gorakhpur', 'Firozabad', 'Jhansi', 'Muzaffarnagar', 'Mathura', 'Shahjahanpur', 'Rampur', 'Modinagar', 'Hapur'] },
        { name: 'Uttarakhand', districts: ['Dehradun', 'Haridwar', 'Rishikesh', 'Nainital', 'Mussoorie', 'Haldwani', 'Roorkee', 'Kashipur', 'Rudrapur', 'Kotdwar', 'Pithoragarh', 'Almora', 'Ranikhet', 'Champawat', 'Bageshwar', 'Chamoli', 'Pauri', 'Tehri', 'Uttarkashi', 'Rudraprayag'] },
        { name: 'West Bengal', districts: ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Malda', 'Baharampur', 'Habra', 'Kharagpur', 'Shantipur', 'Dankuni', 'Dhulian', 'Ranaghat', 'Haldia', 'Raiganj', 'Krishnanagar', 'Nabadwip', 'Medinipur', 'Jalpaiguri', 'Balurghat'] }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full mb-4">
                        <ClipboardDocumentListIcon className="h-8 w-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Create New Task
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Set up a new task with detailed requirements and budget distribution
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                        <div className="flex items-center mb-6">
                            <DocumentTextIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Basic Information
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="lg:col-span-2">
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Task Title *
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter a descriptive task title"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                            </div>

                            {/* Company Selection - Only for Admin */}
                            {user?.role === 'admin' && (
                                <div className="lg:col-span-2">
                                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Company *
                                    </label>
                                    <div className="relative">
                                        <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <select
                                            id="company"
                                            value={selectedCompany}
                                            onChange={(e) => setSelectedCompany(e.target.value)}
                                            required
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none"
                                        >
                                            <option value="">Select a company</option>
                                            {companies.map((company) => (
                                                <option key={company._id} value={company._id}>
                                                    {company.companyName}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="lg:col-span-2">
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Description *
                                </label>
                                <div className="border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all duration-200">
                                    <div className="flex items-center px-4 py-2 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-600 rounded-t-xl">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                type="button"
                                                className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 rounded-lg transition-colors"
                                                onClick={() => {
                                                    const textarea = document.getElementById('description') as HTMLTextAreaElement;
                                                    const start = textarea.selectionStart;
                                                    const end = textarea.selectionEnd;
                                                    const selectedText = textarea.value.substring(start, end);
                                                    const newText = textarea.value.substring(0, start) + `**${selectedText}**` + textarea.value.substring(end);
                                                    setFormData(prev => ({ ...prev, description: newText }));
                                                }}
                                            >
                                                <strong>B</strong>
                                            </button>
                                            <button
                                                type="button"
                                                className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 rounded-lg transition-colors"
                                                onClick={() => {
                                                    const textarea = document.getElementById('description') as HTMLTextAreaElement;
                                                    const start = textarea.selectionStart;
                                                    const end = textarea.selectionEnd;
                                                    const selectedText = textarea.value.substring(start, end);
                                                    const newText = textarea.value.substring(0, start) + `*${selectedText}*` + textarea.value.substring(end);
                                                    setFormData(prev => ({ ...prev, description: newText }));
                                                }}
                                            >
                                                <em>I</em>
                                            </button>
                                            <button
                                                type="button"
                                                className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 rounded-lg transition-colors"
                                                onClick={() => {
                                                    const textarea = document.getElementById('description') as HTMLTextAreaElement;
                                                    const start = textarea.selectionStart;
                                                    const newText = textarea.value.substring(0, start) + '\n• ' + textarea.value.substring(start);
                                                    setFormData(prev => ({ ...prev, description: newText }));
                                                }}
                                            >
                                                List
                                            </button>
                                            <button
                                                type="button"
                                                className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 rounded-lg transition-colors"
                                                onClick={() => {
                                                    const textarea = document.getElementById('description') as HTMLTextAreaElement;
                                                    const start = textarea.selectionStart;
                                                    const newText = textarea.value.substring(0, start) + '\n\n---\n\n' + textarea.value.substring(start);
                                                    setFormData(prev => ({ ...prev, description: newText }));
                                                }}
                                            >
                                                Line
                                            </button>
                                            <button
                                                type="button"
                                                className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 rounded-lg transition-colors"
                                                onClick={() => {
                                                    const textarea = document.getElementById('description') as HTMLTextAreaElement;
                                                    const start = textarea.selectionStart;
                                                    const end = textarea.selectionEnd;
                                                    const selectedText = textarea.value.substring(start, end);
                                                    const newText = textarea.value.substring(0, start) + `\`${selectedText}\`` + textarea.value.substring(end);
                                                    setFormData(prev => ({ ...prev, description: newText }));
                                                }}
                                            >
                                                Code
                                            </button>
                                        </div>
                                        <div className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                                            Markdown supported
                                        </div>
                                    </div>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        required
                                        rows={8}
                                        placeholder="Provide detailed task description and requirements. Use **bold** for emphasis, *italic* for highlights, • for lists, and `code` for code snippets."
                                        className="w-full px-4 py-3 bg-transparent text-gray-900 dark:text-white resize-none focus:outline-none"
                                    />
                                    <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-600 rounded-b-xl">
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            <strong>Tips:</strong> Use **text** for bold, *text* for italic, • for bullet points, `code` for code, and --- for horizontal lines
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="taskUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Task URL *
                                </label>
                                <input
                                    type="url"
                                    id="taskUrl"
                                    name="taskUrl"
                                    value={formData.taskUrl}
                                    onChange={handleChange}
                                    required
                                    placeholder="https://example.com/task"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                            </div>

                            <div className="flex items-center">
                                <label htmlFor="isOnlineTask" className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        id="isOnlineTask"
                                        name="isOnlineTask"
                                        checked={formData.isOnlineTask}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isOnlineTask: e.target.checked }))}
                                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                    />
                                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Online Task (Generate Referral Links for Team Executives)
                                    </span>
                                </label>
                                <div className="ml-2 group relative">
                                    <svg className="h-5 w-5 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="absolute left-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                        Enable this for online tasks to generate unique referral links for team executives only.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Required Skills */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                        <div className="flex items-center mb-6">
                            <UserGroupIcon className="h-6 w-6 text-green-600 dark:text-green-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Required Skills
                            </h2>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Required Skills *
                            </label>
                            <SkillsDropdown
                                selectedSkills={formData.requiredSkills}
                                onSkillsChange={(skills) => handleArrayChange('requiredSkills', skills)}
                                placeholder="Search and select skills required for this task"
                                maxSkills={10}
                                showSearch={true}
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Budget Information */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                        <div className="flex items-center mb-6">
                            <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Budget Distribution
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="totalBudget" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Total Budget Per Task(₹) *
                                </label>
                                <input
                                    type="number"
                                    id="totalBudget"
                                    name="totalBudget"
                                    value={formData.totalBudget}
                                    onChange={handleChange}
                                    required
                                    min="0"
                                    step="0.01"
                                    placeholder="Enter total budget amount"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                            </div>

                            <div className="lg:col-span-2">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                            Budget Scenarios (Range-based Distribution)
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            Create scenarios with different budget distributions for different task count ranges. All scenarios are active by default and will be used based on task completion count.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowBudgetScenarioModal(true)}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                        Add Scenario
                                    </button>
                                </div>

                                {/* Budget Scenarios */}
                                {formData.budgetScenarios.length > 0 && (
                                    <div>
                                        <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">Budget Scenarios</h4>
                                        <div className="space-y-4">
                                            {formData.budgetScenarios.map((scenario, index) => (
                                                <div key={scenario.id} className={`p-4 rounded-xl border ${scenario.isActive
                                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                                                    }`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <h5 className="font-semibold text-gray-900 dark:text-white">{scenario.name}</h5>
                                                                {scenario.isActive && (
                                                                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                                                                        ACTIVE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">{scenario.description}</p>
                                                            <div className="mt-1">
                                                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                    Range: {scenario.range.min} - {scenario.range.max} tasks
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleBudgetScenario(scenario.id)}
                                                                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${scenario.isActive
                                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                                    : 'bg-gray-400 text-white hover:bg-gray-500'
                                                                    }`}
                                                            >
                                                                {scenario.isActive ? 'Active' : 'Inactive'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setEditingScenario(scenario);
                                                                    setNewScenario(scenario);
                                                                    setShowBudgetScenarioModal(true);
                                                                }}
                                                                className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => deleteBudgetScenario(scenario.id)}
                                                                className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                        {Object.entries(scenario.budgetDistribution).map(([role, amount]) => (
                                                            <div key={role}>
                                                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                                    {role.charAt(0).toUpperCase() + role.slice(1).replace(/([A-Z])/g, ' $1')}
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    value={amount}
                                                                    readOnly
                                                                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Geographic Scope */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                        <div className="flex items-center mb-6">
                            <MapPinIcon className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Geographic Scope
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Regions *
                                </label>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {regions.map(region => (
                                        <label key={region} className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.regions.includes(region)}
                                                onChange={(e) => handleRegionChange(region, e.target.checked)}
                                                className="h-5 w-5 min-w-[20px] text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                            />
                                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                                                {region}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Target Designations *
                                </label>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {designations.map(designation => {
                                        // Hide regional head if no regions are selected
                                        if (designation === 'regional_head' && formData.regions.length === 0) {
                                            return null;
                                        }
                                        // Hide state head if no regions and no states are selected
                                        if (designation === 'state_head' && formData.regions.length === 0 && formData.states.length === 0) {
                                            return null;
                                        }

                                        return (
                                            <label key={designation} className="flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.targetDesignations.includes(designation)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            handleArrayChange('targetDesignations', [...formData.targetDesignations, designation]);
                                                        } else {
                                                            handleArrayChange('targetDesignations', formData.targetDesignations.filter(d => d !== designation));
                                                        }
                                                    }}
                                                    className="h-5 w-5 min-w-[20px] text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                                />
                                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                                                    {designation.replace('_', ' ')}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* State and District Selection */}
                        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="stateSelect" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select States (Optional)
                                </label>
                                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-xl p-3 bg-gray-50 dark:bg-gray-700">
                                    {stateData.map(state => (
                                        <label key={state.name} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={formData.states.includes(state.name)}
                                                onChange={(e) => handleStateChange(state.name, e.target.checked)}
                                                className="h-5 w-5 min-w-[20px] text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                            />
                                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                                {state.name}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="districtSelect" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select Districts (Optional)
                                </label>
                                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-xl p-3 bg-gray-50 dark:bg-gray-700">
                                    {stateData
                                        .filter(state => formData.states.includes(state.name))
                                        .flatMap(state => state.districts.map(district => ({ district, state: state.name })))
                                        .map(({ district, state }) => (
                                            <label key={`${state}-${district}`} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.districts.includes(district)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            handleArrayChange('districts', [...formData.districts, district]);
                                                        } else {
                                                            handleArrayChange('districts', formData.districts.filter(d => d !== district));
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                                    {district}
                                                </span>
                                            </label>
                                        ))}
                                    {stateData.filter(state => formData.states.includes(state.name)).length === 0 && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                            Select states first to choose districts
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Timeline */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                        <div className="flex items-center mb-6">
                            <CalendarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Timeline
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Start Date *
                                </label>
                                <input
                                    type="date"
                                    id="startDate"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                            </div>

                            <div>
                                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    End Date *
                                </label>
                                <input
                                    type="date"
                                    id="endDate"
                                    name="endDate"
                                    value={formData.endDate}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Data Collection Fields */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                        <div className="flex items-center mb-6">
                            <CogIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Data Collection Fields
                            </h2>
                        </div>

                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Required Data Fields
                                </h3>
                                <button
                                    type="button"
                                    onClick={addValidationField}
                                    className="inline-flex items-center px-4 py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-800 dark:text-blue-200 text-sm font-medium rounded-lg transition-colors"
                                >
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Add Custom Field
                                </button>
                            </div>
                            <div className="space-y-3 max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-xl p-4 bg-gray-50 dark:bg-gray-700">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {defaultValidationFields.map(field => (
                                        <label key={field} className="flex items-center p-2 hover:bg-white dark:hover:bg-gray-600 rounded-lg transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={formData.validationRules.requiredFields.includes(field)}
                                                onChange={() => handleValidationFieldChange(field)}
                                                className="h-5 w-5 min-w-[20px] text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                            />
                                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                                {field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </span>
                                        </label>
                                    ))}
                                </div>

                                {/* Custom Fields */}
                                {formData.validationRules.requiredFields.filter(field => !defaultValidationFields.includes(field)).length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-500">
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Custom Fields:</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {formData.validationRules.requiredFields.filter(field => !defaultValidationFields.includes(field)).map(field => (
                                                <div key={field} className="flex items-center p-2 bg-white dark:bg-gray-600 rounded-lg">
                                                    <input
                                                        type="checkbox"
                                                        checked={true}
                                                        onChange={() => handleValidationFieldChange(field)}
                                                        className="h-5 w-5 min-w-[20px] text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex-1">
                                                        {field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleValidationFieldChange(field)}
                                                        className="ml-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
                                                        title="Remove field"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            <p><strong>Note:</strong> Select the data fields that users need to collect when completing this task. These fields will be validated when users submit their data.</p>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={() => window.history.back()}
                            className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Creating Task...
                                </>
                            ) : (
                                <>
                                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                                    Create Task
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Budget Scenario Modal */}
            {showBudgetScenarioModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {editingScenario ? 'Edit Budget Scenario' : 'Add Budget Scenario'}
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowBudgetScenarioModal(false);
                                        setEditingScenario(null);
                                    }}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Scenario Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Scenario Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={editingScenario?.name || newScenario.name || ''}
                                            onChange={(e) => {
                                                if (editingScenario) {
                                                    setEditingScenario({ ...editingScenario, name: e.target.value });
                                                } else {
                                                    setNewScenario({ ...newScenario, name: e.target.value });
                                                }
                                            }}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Range Type
                                        </label>
                                        <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                            Task Count Range (e.g., 1-500, 501-1000, etc.)
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        value={editingScenario?.description || newScenario.description || ''}
                                        onChange={(e) => {
                                            if (editingScenario) {
                                                setEditingScenario({ ...editingScenario, description: e.target.value });
                                            } else {
                                                setNewScenario({ ...newScenario, description: e.target.value });
                                            }
                                        }}
                                        rows={3}
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Range Settings */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Minimum Task Count *
                                        </label>
                                        <input
                                            type="number"
                                            value={editingScenario?.range?.min || newScenario.range?.min || 1}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 1;
                                                if (editingScenario) {
                                                    setEditingScenario({
                                                        ...editingScenario,
                                                        range: { ...editingScenario.range, min: value }
                                                    });
                                                } else {
                                                    setNewScenario({
                                                        ...newScenario,
                                                        range: { ...newScenario.range!, min: value }
                                                    });
                                                }
                                            }}
                                            min="1"
                                            step="1"
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Maximum Task Count *
                                        </label>
                                        <input
                                            type="number"
                                            value={editingScenario?.range?.max || newScenario.range?.max || 500}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 500;
                                                if (editingScenario) {
                                                    setEditingScenario({
                                                        ...editingScenario,
                                                        range: { ...editingScenario.range, max: value }
                                                    });
                                                } else {
                                                    setNewScenario({
                                                        ...newScenario,
                                                        range: { ...newScenario.range!, max: value }
                                                    });
                                                }
                                            }}
                                            min="1"
                                            step="1"
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                {/* Budget Distribution */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Budget Distribution</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {Object.entries(editingScenario?.budgetDistribution || newScenario.budgetDistribution || {}).map(([role, amount]) => (
                                            <div key={role}>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">
                                                    {role.replace(/([A-Z])/g, ' $1').trim()}
                                                </label>
                                                <input
                                                    type="number"
                                                    value={amount as number}
                                                    onChange={(e) => {
                                                        const value = parseFloat(e.target.value) || 0;
                                                        const currentDistribution = editingScenario?.budgetDistribution || newScenario.budgetDistribution || {
                                                            teamExecutive: 0,
                                                            teamLeader: 0,
                                                            districtHead: 0,
                                                            stateHead: 0,
                                                            regionalHead: 0,
                                                            admin: 0,
                                                        };
                                                        const updatedDistribution = {
                                                            ...currentDistribution,
                                                            [role]: value
                                                        };
                                                        if (editingScenario) {
                                                            setEditingScenario({
                                                                ...editingScenario,
                                                                budgetDistribution: updatedDistribution
                                                            });
                                                        } else {
                                                            setNewScenario({
                                                                ...newScenario,
                                                                budgetDistribution: updatedDistribution
                                                            });
                                                        }
                                                    }}
                                                    min="0"
                                                    step="0.01"
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowBudgetScenarioModal(false);
                                            setEditingScenario(null);
                                        }}
                                        className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (editingScenario) {
                                                updateBudgetScenario(editingScenario.id, editingScenario);
                                            } else {
                                                addBudgetScenario();
                                            }
                                        }}
                                        className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors"
                                    >
                                        {editingScenario ? 'Update Scenario' : 'Add Scenario'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskForm;